import React, { useState, useEffect, useRef } from "react";
import { FaEdit, FaDownload, FaExpand, FaCheck, FaUndo, FaTimes } from "react-icons/fa";
import DownloadImageModel from "./DownloadImageModel";
import History from "../History/History";
import { useImage } from "../../context/ImageContext";
import ImageModal from "./ImageModal";
import { lowQualityToHighQuality } from "../../config/aws";
import "../ImageEditor/ImageEditor.css";


const MainCreate = ({ editUploadedImages, setEditUploadedImages, allCreateHistory, allEditHistory, setAllEditHistory, createPageNumber, setCreatePageNumber, setCreditsAvailable, creditsAvailable, createNumberOfImages, createLoadingCircle, selectedGroup, setSelectedGroup, activeIndex, setActiveIndex, modalImage, setModalImage, selectedImageIndex, setSelectedImageIndex, imageDimensions, setImageDimensions, showDownloadModal, setShowDownloadModal, containerRef
}) => {

  const { activeTab, setActiveTab } = useImage();

  // Function to calculate image dimensions while maintaining aspect ratio
  const calculateImageSize = (naturalWidth, naturalHeight, maxWidth = 800, maxHeight = 600) => {
    const aspectRatio = naturalWidth / naturalHeight;

    // For horizontal/landscape images, use a larger max width
    let maxDisplayWidth = maxWidth;
    if (aspectRatio > 1) {
      // Horizontal image - scale up the width
      maxDisplayWidth = 800; // Increased from 600 to 800 for horizontal images
    }

    let width = maxDisplayWidth;
    let height = maxDisplayWidth / aspectRatio;

    if (height > maxHeight) {
      height = maxHeight;
      width = maxHeight * aspectRatio;
    }

    return { width, height };
  };

  // Function to handle image load and calculate dimensions
  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.target;
    const dimensions = calculateImageSize(naturalWidth, naturalHeight);
    setImageDimensions(dimensions);
  };

  useEffect(() => {
    const loadHighQualityImages = async () => {
      if (allCreateHistory.length > 0) {
        const updatedImages = [...allCreateHistory[0].images];
        for (let i = 0; i < updatedImages.length; i++) {
          // console.log("in MainCreate useEffect allCreateHistory changed    fetching high quality images");
          updatedImages[i] = {
            ...updatedImages[i],
            src: await lowQualityToHighQuality(updatedImages[i].src)
          };
        }
        setSelectedGroup(updatedImages);
      }
    };

    loadHighQualityImages();
  }, [allCreateHistory]);

  useEffect(() => {
    selectedGroup.forEach(image => {
      if (image.src.includes("lowQuality")) {
        // console.log("in MainCreate useEffect selectedGroup changed    fetching high quality images");
        lowQualityToHighQuality(image.src).then(highQualityUrl => {
          image.src = highQualityUrl;
        });
      }
    });
  }, [selectedGroup]);

  const LoadingState = () => (
    <div className="w-full max-w-6xl space-y-6  p-4 sm:p-6">
      <div className="flex justify-center" style={{ background: '#f8fafc' }}>
        <div className="relative overflow-hidden shadow-lg w-full max-w-5xl">
          <div
            className="relative rounded-lg overflow-hidden  flex items-center justify-center"
            style={{
              height: 'clamp(300px, 60vh, 500px)',
              width: '100%',
              maxWidth: '100%'
            }}
          >
            <div
              className="w-16 h-16 rounded-full"
              style={{
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #7c3aed',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Loading Thumbnail Strip */}
      <div className="flex justify-center">
        <div className="flex space-x-3 sm:space-x-4">
          {[...Array(createNumberOfImages)].map((_, index) => (
            <div
              key={index}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gray-200 shadow-sm flex items-center justify-center"
            >
              <div
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                style={{
                  border: '3px solid #d1d5db',
                  borderTop: '3px solid #7c3aed',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              ></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );


  return (
    <div className="flex flex-col h-full w-full overflow-hidden" ref={containerRef}>
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center mt-16 min-h-0 w-full max-w-full">
        {createLoadingCircle ? (
          <LoadingState />
        ) : selectedGroup.length > 0 ? (
          <div className="image-editor-body p-0">
            <div className="my-0 py-0 image-editor-card">
              <div className="editor-grid my-0 py-0">
                <div className="image-tile">
                  <div className="image-wrapper">
                    <div id="mainImage" className="main-image relative">
                      <img
                        src={selectedGroup[selectedImageIndex]?.src}
                        alt={selectedGroup[selectedImageIndex]?.alt}
                        className="w-full h-full object-contain"
                        onLoad={handleImageLoad}
                      />

                      {/* Action Buttons */}
                      <div className="absolute top-4 right-4 flex space-x-2 z-20">
                        <button
                          className="bg-white/90 backdrop-blur-sm p-2.5 rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-110 hover:shadow-xl"
                          onClick={() => setShowDownloadModal(true)}
                        >
                          <img
                            src="https://img.icons8.com/?size=100&id=20FjgTazh8FG&format=png&color=000000"
                            alt="Download"
                            className="w-6 h-6"
                          />
                        </button>
                        <button
                          className="bg-white/90 backdrop-blur-sm p-2.5 rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-110 hover:shadow-xl"
                          onClick={async () => {
                            setEditUploadedImages([{ src: selectedGroup[selectedImageIndex]?.src, alt: "High Quality Image" }]);
                            setActiveTab('edit');
                            setAllEditHistory([{ images: [{ src: selectedGroup[selectedImageIndex]?.src, alt: "Image from Create History" }] }, ...allEditHistory]);
                          }}
                        >
                          <img
                            src="https://img.icons8.com/?size=100&id=ky9dlsSpTXp0&format=png&color=1A1A1A"
                            alt="Edit"
                            className="w-6 h-6"
                          />
                        </button>
                      </div>

                      {/* Download Modal Overlay */}
                      {showDownloadModal && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                          <DownloadImageModel
                            imageData={{ src: selectedGroup[selectedImageIndex]?.src, alt: selectedGroup[selectedImageIndex]?.alt }}
                            onClose={() => setShowDownloadModal(false)}
                            creditsAvailable={creditsAvailable}
                            setCreditsAvailable={setCreditsAvailable}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="toolbar-tile">
                  <div className="toolbar">
                    <div className="thumbnail-list" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {selectedGroup.map((image, index) => (
                        <img
                          key={index}
                          src={image.src}
                          alt={image.alt}
                          className={`thumbnail ${index === selectedImageIndex ? 'selected' : ''}`}
                          onClick={() => setSelectedImageIndex(index)}
                          style={{ margin: '0 6px' }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 bg-white rounded-xl p-6 sm:p-8 shadow-lg border border-gray-100 max-w-md mx-auto w-full">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <img
                src="https://img.icons8.com/?size=100&id=ky9dlsSpTXp0&format=png&color=1A1A1A"
                alt="Edit"
                className="w-6 h-6"
              />
            </div>
            <p className="text-base font-medium mb-2">No images selected</p>
            <p className="text-sm text-gray-400">Select an image from the history to view and edit</p>
          </div>
        )}
      </div>

      {/* History Section */}
      <div className="flex-shrink-0">
        <History
          All_History={allCreateHistory}
          setSelectedGroup={setSelectedGroup}
          setModalImage={setModalImage}
          setEditUploadedImages={setEditUploadedImages}
          setActiveTab={setActiveTab}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          createPageNumber={createPageNumber}
          setCreatePageNumber={setCreatePageNumber}
          containerRef={containerRef}
          allEditHistory={allEditHistory}
          setAllEditHistory={setAllEditHistory}
          activeTab={activeTab}
        />
      </div>

      {modalImage && (
        <ImageModal
          image={modalImage}
          onClose={() => setModalImage(null)}
        />
      )}
    </div>
  );
};

export default MainCreate;